/**
 * Tapsody SSO Detection Utility
 * 
 * Checks if a VJB user has existing Tapsody data and fetches it for auto-fill.
 * Tapsody and VJB share the same Firebase Auth - same email/password = same user.
 * 
 * Data structure in Tapsody:
 * - bandMembers/{uid} → { bandId }
 * - bands/{bandId} → { name, members, genres, location, ... }
 * - bands/{bandId}/members/{uid} → { displayName, role, ... }
 */

import { ref, get } from 'firebase/database';
import { database } from '../../shared/firebase';

/**
 * Check if a user has Tapsody data and fetch it
 * @param {string} uid - Firebase Auth user ID
 * @returns {Promise<TapsodyData|null>}
 */
export async function detectTapsodyUser(uid) {
  if (!uid) return null;

  try {
    // Step 1: Check if user has band membership
    const membershipSnapshot = await get(ref(database, `bandMembers/${uid}`));
    
    if (!membershipSnapshot.exists()) {
      // No Tapsody data - user is VJB-only
      return null;
    }

    const { bandId } = membershipSnapshot.val();
    if (!bandId) return null;

    // Step 2: Fetch band data
    const bandSnapshot = await get(ref(database, `bands/${bandId}`));
    
    if (!bandSnapshot.exists()) {
      // Band was deleted but membership remains (edge case)
      return null;
    }

    const bandData = bandSnapshot.val();
    const memberData = bandData.members?.[uid] || {};

    // Step 3: Compile Tapsody data for VJB profile
    return {
      isTapsodyUser: true,
      bandIds: [bandId],
      
      // User-specific data
      displayName: memberData.displayName || null,
      role: memberData.role || 'member', // 'leader' or 'member'
      
      // Band data (for pre-filling VJB profile)
      bandName: bandData.name || null,
      genres: bandData.genres || [],
      genreCategories: bandData.genreCategories || [],
      location: bandData.location || null, // City
      region: bandData.region || null, // Province/region
      
      // Additional context
      bandCreatedAt: bandData.createdAt || null,
      memberJoinedAt: memberData.joinedAt || null,
      isLeader: memberData.role === 'leader',
    };

  } catch (error) {
    console.error('Error detecting Tapsody user:', error);
    return null;
  }
}

/**
 * Map Tapsody genres to VJB genres
 * Tapsody uses subcategory-level genres, VJB uses broader categories
 */
const TAPSODY_TO_VJB_GENRE_MAP = {
  // Rock subcategories
  'Classic Rock': 'Rock',
  'Alternative Rock': 'Rock',
  'Indie Rock': 'Indie',
  'Hard Rock': 'Rock',
  'Progressive Rock': 'Rock',
  'Punk Rock': 'Punk',
  'Grunge': 'Rock',
  'Garage Rock': 'Rock',
  
  // Metal subcategories
  'Heavy Metal': 'Metal',
  'Thrash Metal': 'Metal',
  'Death Metal': 'Metal',
  'Black Metal': 'Metal',
  'Doom Metal': 'Metal',
  'Power Metal': 'Metal',
  'Metalcore': 'Metal',
  'Nu Metal': 'Metal',
  
  // Pop subcategories
  'Synth Pop': 'Pop',
  'Indie Pop': 'Pop',
  'Dance Pop': 'Pop',
  'Electropop': 'Electronic',
  'Art Pop': 'Pop',
  
  // Jazz subcategories
  'Bebop': 'Jazz',
  'Swing': 'Jazz',
  'Fusion': 'Jazz',
  'Smooth Jazz': 'Jazz',
  'Free Jazz': 'Jazz',
  
  // Blues subcategories
  'Delta Blues': 'Blues',
  'Chicago Blues': 'Blues',
  'Electric Blues': 'Blues',
  'Blues Rock': 'Blues',
  
  // Electronic subcategories
  'House': 'Electronic',
  'Techno': 'Electronic',
  'Drum and Bass': 'Electronic',
  'Dubstep': 'Electronic',
  'Trance': 'Electronic',
  'Ambient': 'Electronic',
  'IDM': 'Electronic',
  
  // Hip-hop subcategories
  'Rap': 'Hip-hop',
  'Trap': 'Hip-hop',
  'Boom Bap': 'Hip-hop',
  'Conscious Hip-Hop': 'Hip-hop',
  
  // Folk subcategories
  'Americana': 'Folk',
  'Celtic': 'Folk',
  'Bluegrass': 'Country',
  
  // Funk/Soul
  'Disco': 'Funk',
  'Neo-Soul': 'Soul',
  'Motown': 'Soul',
  
  // Direct mappings
  'Rock': 'Rock',
  'Pop': 'Pop',
  'Metal': 'Metal',
  'Jazz': 'Jazz',
  'Blues': 'Blues',
  'Funk': 'Funk',
  'Soul': 'Soul',
  'R&B': 'R&B',
  'Hip-hop': 'Hip-hop',
  'Electronic': 'Electronic',
  'Folk': 'Folk',
  'Country': 'Country',
  'Klassiek': 'Klassiek',
  'Punk': 'Punk',
  'Indie': 'Indie',
  'Reggae': 'Reggae',
};

/**
 * Convert Tapsody genres to VJB genre format
 * @param {string[]} tapsodyGenres - Genres from Tapsody
 * @returns {string[]} - VJB-compatible genres (deduplicated)
 */
export function mapTapsodyGenres(tapsodyGenres) {
  if (!tapsodyGenres || tapsodyGenres.length === 0) return [];
  
  const vjbGenres = new Set();
  
  for (const genre of tapsodyGenres) {
    const mapped = TAPSODY_TO_VJB_GENRE_MAP[genre];
    if (mapped) {
      vjbGenres.add(mapped);
    }
  }
  
  return Array.from(vjbGenres);
}

/**
 * Map Tapsody region to VJB province code
 */
const TAPSODY_REGION_TO_PROVINCE = {
  'Noord-Holland': 'NH',
  'Zuid-Holland': 'ZH',
  'Utrecht': 'UT',
  'Noord-Brabant': 'NB',
  'Limburg': 'LI',
  'Gelderland': 'GE',
  'Overijssel': 'OV',
  'Flevoland': 'FL',
  'Friesland': 'FR',
  'Groningen': 'GR',
  'Drenthe': 'DR',
  'Zeeland': 'ZE',
  // Belgian provinces
  'Antwerpen': 'BE-AN',
  'Limburg (BE)': 'BE-LI',
  'Oost-Vlaanderen': 'BE-OV',
  'West-Vlaanderen': 'BE-WV',
  'Vlaams-Brabant': 'BE-VB',
};

/**
 * Convert Tapsody region to VJB province code
 * @param {string} tapsodyRegion - Region name from Tapsody
 * @returns {string|null} - VJB province code
 */
export function mapTapsodyRegion(tapsodyRegion) {
  if (!tapsodyRegion) return null;
  return TAPSODY_REGION_TO_PROVINCE[tapsodyRegion] || null;
}

/**
 * Prepare VJB profile data from Tapsody data
 * Used to auto-fill the profile form for Tapsody users
 * @param {TapsodyData} tapsodyData - Data from detectTapsodyUser
 * @returns {Partial<VJBProfile>}
 */
export function preparePrefillFromTapsody(tapsodyData) {
  if (!tapsodyData) return {};
  
  return {
    // Use band name for bands, display name for musicians
    displayName: tapsodyData.isLeader ? tapsodyData.bandName : tapsodyData.displayName,
    
    // Location
    city: tapsodyData.location || '',
    province: mapTapsodyRegion(tapsodyData.region) || '',
    
    // Genres
    genres: mapTapsodyGenres(tapsodyData.genres),
    
    // Suggest type based on role
    suggestedType: tapsodyData.isLeader ? 'band' : 'musician',
    
    // Tapsody linking data
    tapsodyLinked: true,
    tapsodyBandIds: tapsodyData.bandIds,
    tapsodyRole: tapsodyData.role,
  };
}
